import { useServices } from '@/app/providers';
import type { Unit, User } from '@yawi/types';
import { useEffect, useRef, useState } from 'react';

export type GlobalSearchResult =
  | { kind: 'unit'; id: string; label: string; sublabel: string; entity: Unit }
  | { kind: 'user'; id: string; label: string; sublabel: string; entity: User };

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 220;
const LIMIT = 5;

function buildUnitLabel(unit: Unit): string {
  const parts = [unit.name, unit.tower && `Torre ${unit.tower}`, unit.number && `#${unit.number}`].filter(Boolean);
  return parts.join(' · ') || unit.id;
}

function buildUnitSublabel(unit: Unit): string {
  const segments = [unit.type === 'APARTMENT' ? 'Departamento' : 'Casa'];
  if (unit.floor) segments.push(`Piso ${unit.floor}`);
  if (unit.block) segments.push(`Bloque ${unit.block}`);
  return segments.join(' · ');
}

export function useGlobalSearch(query: string) {
  const { unitRepo, userRepo } = useServices();
  const [results, setResults] = useState<GlobalSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      requestIdRef.current += 1; // invalida la búsqueda en vuelo: no debe repintar resultados viejos
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const requestId = ++requestIdRef.current;
    const handle = window.setTimeout(async () => {
      const [unitsRes, usersRes] = await Promise.allSettled([
        unitRepo.getUnits({ page: 1, limit: LIMIT, search: trimmed }),
        userRepo.getUsers({ page: 1, limit: LIMIT, search: trimmed }),
      ]);

      if (requestIdRef.current !== requestId) return;

      const next: GlobalSearchResult[] = [];

      if (unitsRes.status === 'fulfilled') {
        for (const unit of unitsRes.value.data) {
          next.push({
            kind: 'unit',
            id: unit.id,
            label: buildUnitLabel(unit),
            sublabel: buildUnitSublabel(unit),
            entity: unit,
          });
        }
      }

      if (usersRes.status === 'fulfilled') {
        for (const user of usersRes.value.data) {
          next.push({
            kind: 'user',
            id: user.id,
            label: user.name,
            sublabel: user.email,
            entity: user,
          });
        }
      }

      setResults(next);
      setLoading(false);
    }, DEBOUNCE_MS);

    return () => {
      window.clearTimeout(handle);
    };
  }, [query, unitRepo, userRepo]);

  return { results, loading };
}
